"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { resolveI18nText, resolveI18nRichtext, DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import {
  IPlatformItem,
  IPlatformsFooter,
  IPlatformsGroup,
} from "@/sanity/lib/definitions";
import { GroqImage } from "@/helpers/Image";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@/shadcn/ui/dialog";
import { resolveNewBadgeLabel } from "./helpers";
import { resolveLink } from "@/sanity/globals/CTA";

/**
 * Optional code-owned extra cards per group key (e.g. { futures: <Card/> }),
 * rendered as the group's second tile. Lets a page add a platform card that
 * does not exist in the Sanity document (the Tradovate dialog card).
 */
export type PlatformGroupExtras = Record<string, React.ReactNode>;

export default function PricingPlatformsBlock({
  data,
  language,
  filter,
  groupExtras,
}: {
  data: IPlatformsFooter;
  language?: string;
  filter?: { assetKey?: string };
  groupExtras?: PlatformGroupExtras;
}) {
  const chip = resolveI18nText(data.chip, data.chipI18n, language);
  const content = resolveI18nRichtext(data.content, data.contentI18n, language);

  const groups = filter?.assetKey
    ? data.groups?.filter((g) => g.key === filter.assetKey)
    : data.groups;

  return (
    <section className="relative w-full pt-[6rem] md:pt-[10rem] isolate">
      {/* Top divider (Figma node 4493-840) */}
      <div
        className="absolute left-0 right-0 top-0 h-[1px] bg-black/10 z-[1]"
        aria-hidden="true"
      />

      {/* Base white layer so mix-blend-exclusion has something to blend against */}
      <div
        className="pointer-events-none absolute inset-0 bg-white"
        aria-hidden="true"
      />

      {/* Dots gradient background — exclusion blend (desktop only) */}
      <div
        className="pointer-events-none absolute inset-0 hidden md:block bg-no-repeat bg-cover bg-center mix-blend-exclusion"
        style={{
          backgroundImage: "url(/pricing-section-v2/dots-gradient-bg.png)",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 flex flex-col md:flex-row md:items-start md:justify-between gap-[4rem] md:gap-[6rem]">
        {/* Left column — heading */}
        <div className="w-full md:w-[min(44.25rem,45%)] flex flex-col gap-[1.125rem] max-md:items-center max-md:text-center">
          {chip && (
            <p className="mb-0 font-blacker italic text-[1.75rem] md:text-[2rem] leading-[1.5] text-black">
              {chip}
            </p>
          )}
          {content && (
            <div className="richtext md:richtext-start flex flex-col gap-[1.5rem] md:gap-[2rem]">
              <PortableText value={content} components={richTextComponent} />
            </div>
          )}
        </div>

        {/* Right column — groups */}
        <div className="w-full md:w-[min(68.75rem,50%)] flex flex-col gap-[5rem] md:gap-[7rem]">
          {groups?.map((group, groupIdx) => (
            <PlatformsGroupSection
              key={group._key ?? group.key}
              group={group}
              language={language}
              dimmed={groupIdx > 0}
              extra={groupExtras?.[group.key ?? ""]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PlatformsGroupSection({
  group,
  language,
  dimmed,
  extra,
}: {
  group: IPlatformsGroup;
  language?: string;
  dimmed: boolean;
  extra?: React.ReactNode;
}) {
  const groupLabel = resolveI18nText(group.label, group.labelI18n, language);
  return (
    <div className="flex flex-col gap-[1.5rem] md:gap-[2rem] w-full">
      {groupLabel && (
        <p
          className={
            "mb-0 text-[1.75rem] md:text-[2rem] leading-normal uppercase text-black font-geist font-medium " +
            (dimmed ? "opacity-[0.64]" : "opacity-80")
          }
        >
          {groupLabel}
        </p>
      )}
      {group.items?.length || extra ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.25rem]">
          {group.items?.slice(0, 1).map((item) => (
            <PlatformCard
              key={item._key ?? item.name}
              item={item}
              language={language}
            />
          ))}
          {extra}
          {group.items?.slice(1).map((item) => (
            <PlatformCard
              key={item._key ?? item.name}
              item={item}
              language={language}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PlatformCard({
  item,
  language,
}: {
  item: IPlatformItem;
  language?: string;
}) {
  const name = resolveI18nText(item.name, item.nameI18n, language);
  const hasPopup = !!item.popupBullets?.length || !!item.popupImage;
  // No href means no destination — never fall back to "/", which would send the
  // visitor to the homepage in a new tab. The card then renders inert.
  const href = item.href?.trim();
  const link = href ? resolveLink(href, language || DEFAULT_LANGUAGE) : "";
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  const card = (
    <div className="group relative flex w-full items-center justify-between gap-[1.25rem] md:gap-[1.5rem] rounded-[0.5rem] border border-black/[0.12] bg-[#f4f4f4] pl-[2.25rem] pr-[1.25rem] py-[1.25rem] md:pl-[3rem] md:pr-[1.75rem] md:py-[1.75rem] hover:bg-[#efefef] transition-colors">
      <div className="flex items-center gap-[1.25rem] md:gap-[1.5rem] min-w-0">
        {item.logo && (
          <div className="relative h-[3rem] md:h-[3.75rem] w-[16rem] md:w-[18rem] flex items-center justify-start shrink-0">
            <GroqImage
              src={item.logo}
              alt={name ?? ""}
              className="max-h-full max-w-full !h-auto !w-auto object-contain object-left"
            />
          </div>
        )}
      </div>
      <div className="inline-flex items-center justify-center bg-white rounded-[0.25rem] p-[1rem] md:p-[1.25rem] text-black shrink-0 transition-colors group-hover:bg-black group-hover:text-white">
        <ArrowUpRightIcon />
      </div>
      {item.isNew && (
        <span className="pointer-events-none absolute -top-[1.25rem] md:-top-[1.5rem] -right-[0.5rem] md:-right-[0.75rem] inline-flex items-center justify-center px-[1rem] py-[0.375rem] md:px-[1.125rem] md:py-[0.5rem] rounded-[7.5rem] bg-[#1aa87f] text-white text-[1.5rem] md:text-[1.75rem] leading-[1.3] tracking-[-0.075rem] md:tracking-[-0.0875rem] font-geist font-normal whitespace-nowrap shadow-sm">
          {resolveNewBadgeLabel(language)}
        </span>
      )}
    </div>
  );

  if (hasPopup) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <button type="button" className="w-full text-left cursor-pointer">
            {card}
          </button>
        </DialogTrigger>
        <DialogContent className="!w-[min(120rem,calc(100%-4rem))] bg-white rounded-[0.75rem] p-[2rem] pb-[3.75rem]">
          <PlatformPopupContent
            item={item}
            name={name ?? ""}
            language={language}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return finalLink ? (
    <Link
      key={item._key ?? item.name}
      href={finalLink}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full"
    >
      {card}
    </Link>
  ) : (
    <div key={item._key ?? item.name} className="w-full">
      {card}
    </div>
  );
}

const POPUP_CTA_CLASSES =
  "w-full inline-flex items-center justify-center bg-black border border-black/[0.12] rounded-[0.25rem] px-[3rem] py-[2rem] md:py-[2.25rem] text-white font-instrument-sans font-semibold text-[2rem] leading-[1.35] tracking-[-0.04rem] capitalize hover:bg-black/90 transition-colors";

export function PlatformPopupContent({
  item,
  name,
  language,
}: {
  item: IPlatformItem;
  name: string;
  language?: string;
}) {
  const bulletColor = item.popupBulletColor || "#103037";
  const ctaLabel =
    resolveI18nText(item.popupCtaLabel, item.popupCtaLabelI18n, language) ||
    "Back to website";
  // Platforms without a link (empty, null or missing href in Sanity) keep the
  // button, but it just dismisses the popup — same as the X — instead of
  // navigating to "/" in a new tab, which is what a `|| "/"` fallback did.
  const href = item.href?.trim();
  const link = href ? resolveLink(href, language || DEFAULT_LANGUAGE) : "";
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  return (
    <div className="flex flex-col gap-[3rem] items-center w-full">
      {/* Screenshot */}
      {item.popupImage && (
        <div className="relative w-full aspect-[615/335] rounded-[0.5rem] overflow-hidden">
          <GroqImage
            src={item.popupImage}
            alt={name}
            className="!w-full !h-full object-cover"
          />
        </div>
      )}

      {/* Header + bullets + CTA */}
      <div className="flex flex-col gap-[4.5rem] items-start w-full px-[1.75rem]">
        <div className="flex flex-col gap-[2.5rem] md:gap-[3rem] w-full">
          {/* Logo */}
          <div className="flex flex-col gap-[2rem] w-full">
            {item.logo && (
              <div className="relative h-[3rem] md:h-[4rem] w-[18rem] md:w-[22rem] flex items-center justify-start">
                <GroqImage
                  src={item.logo}
                  alt={name}
                  className="max-h-full max-w-full !h-auto !w-auto object-contain object-left"
                />
              </div>
            )}
            <div className="h-[1px] w-full bg-black/10" aria-hidden="true" />
          </div>

          {/* Bullets */}
          {item.popupBullets?.length ? (
            <div className="flex flex-col gap-[1.25rem] w-full">
              {item.popupBullets.map((bullet) => {
                const text = resolveI18nText(
                  bullet.text,
                  bullet.textI18n,
                  language,
                );
                return (
                  <div
                    key={bullet._key ?? bullet.text}
                    className="flex items-start gap-[1.25rem]"
                  >
                    <span
                      className="inline-flex items-center justify-center shrink-0 w-[1.375rem] h-[1.375rem] mt-[0.625rem]"
                      aria-hidden="true"
                    >
                      <span
                        className="block w-[1rem] h-[1rem] rotate-45"
                        style={{ backgroundColor: bulletColor }}
                      />
                    </span>
                    <p className="mb-0 flex-1 font-geist font-normal text-[2.25rem] leading-[1.32] md:leading-[1.5] text-[#103037]">
                      {text}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* CTA — a link when the platform has one, otherwise a close button. */}
        {href ? (
          finalLink && (
            <Link
              href={finalLink}
              target="_blank"
              rel="noopener noreferrer"
              className={POPUP_CTA_CLASSES}
            >
              {ctaLabel}
            </Link>
          )
        ) : (
          <DialogClose asChild>
            <button type="button" className={POPUP_CTA_CLASSES}>
              {ctaLabel}
            </button>
          </DialogClose>
        )}
      </div>
    </div>
  );
}

export function ArrowUpRightIcon({ className }: { className?: string } = {}) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={className ?? "w-[2rem] h-[2rem]"}
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 12L12 4M12 4H5.5M12 4V10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
